import type { Dispatch, SetStateAction } from 'react';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isBrowser } from '~/utils';

interface IGSetupGuideContext {
  hideOverlay?: boolean;
  highlightedSetupId: string | null;
  expandedId: string | undefined;
  setExpandedId: Dispatch<SetStateAction<string | undefined>>;
  handleOpenHighLight: (id: string) => void;
  handleCloseHighLight: () => void;
}

export const GSetupGuideContext = createContext<IGSetupGuideContext | undefined>(undefined);

export interface GSetupGuideProviderProps {
  children: React.ReactNode;
  hideOverlay?: boolean;
}

export const GSetupGuideProvider: React.FC<GSetupGuideProviderProps> = ({ children }) => {
  const [highlightedSetupId, setHighlightedSetupId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | undefined>(undefined);

  const handleOpenHighLight = useCallback((id: string) => {
    setHighlightedSetupId(id);
  }, []);

  const handleCloseHighLight = useCallback(() => {
    setHighlightedSetupId(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      highlightedSetupId,
      expandedId,
      setExpandedId,
      handleOpenHighLight,
      handleCloseHighLight,
    }),
    [highlightedSetupId, expandedId, setExpandedId, handleOpenHighLight, handleCloseHighLight],
  );

  useEffect(() => {
    if (!highlightedSetupId || !isBrowser()) return;

    const element = document.querySelector('.GSetupGuide');
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedSetupId]);

  return <GSetupGuideContext.Provider value={contextValue}>{children}</GSetupGuideContext.Provider>;
};

export const useGSetupGuideContext = () => {
  const context = useContext(GSetupGuideContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context as IGSetupGuideContext;
};
